/**
 * Staging Areas layer (R.4.2, operator-cop-doctrine §2.5/§6.4 decision (a)). The
 * Logistics "staging area" object the IMS Resource Status Lifecycle's `staging`
 * state attaches to — a forward operating base / receiving site declared on an
 * incident. Renders each OPEN staging area as an ambient depot/tent glyph. AMBIENT
 * register only: never pulses, never competes with the anomaly surface above
 * (§3B.8) — a staging area is context, not an alert.
 *
 * Imperative installer (MapView owns the viewer), mirroring DroneCoverageLayer:
 * requestRefresh(bbox) / setVisible / pick → StagingArea → the staging-area side
 * panel. Fetched by bbox (open areas in view) so it follows the same bbox-driven
 * pattern as every other ambient layer.
 */
import * as Cesium from 'cesium';
import { fetchStagingAreasInBbox, type BboxQuery } from '../api';
import type { StagingArea, StagingAreaListResponse } from '@tsr/shared-types';
import { tintedSvgDataUrl } from '../svgIcon';

const REFRESH_DEBOUNCE_MS = 300;
const STAGING_PREFIX = 'staging:';

/** Ambient amber-slate — a logistics/depot tone, distinct from drone purple and
 *  the warm anomaly palette. */
export const STAGING_AMBIENT_COLOR = '#d8b46a';

/** A simple depot/tent glyph (peaked roof + base). fill="currentColor" → tinted. */
export const STAGING_GLYPH_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">' +
  '<path fill="currentColor" d="M12 3 1 12h3v8h6v-5h4v5h6v-8h3z"/>' +
  '<path fill="currentColor" d="M12 3 1 12h3v1l8-7 8 7v-1z" opacity="0.5"/>' +
  '</svg>';

export interface StagingAreasLayerOptions {
  onCount?: (n: number) => void;
  getVisible?: () => boolean;
  fetcher?: (bbox: BboxQuery, signal: AbortSignal) => Promise<StagingAreaListResponse>;
}

export interface StagingAreasLayerHandle {
  requestRefresh(bbox: BboxQuery): void;
  refreshNow(bbox: BboxQuery): Promise<void>;
  setVisible(visible: boolean): void;
  pick(entity: unknown): StagingArea | null;
  destroy(): void;
}

export function installStagingAreasLayer(
  viewer: Cesium.Viewer,
  opts: StagingAreasLayerOptions = {},
): StagingAreasLayerHandle {
  const fetcher = opts.fetcher ?? fetchStagingAreasInBbox;
  const ds = new Cesium.CustomDataSource('staging-areas');
  void viewer.dataSources.add(ds);
  const glyphUrl = tintedSvgDataUrl('staging-glyph', STAGING_GLYPH_SVG, STAGING_AMBIENT_COLOR);

  const entityToArea = new Map<Cesium.Entity, StagingArea>();
  let inflight: AbortController | null = null;
  let debounceTimer: ReturnType<typeof setTimeout> | null = null;
  let destroyed = false;
  let loadGen = 0;

  const render = (areas: StagingArea[]): void => {
    ds.entities.removeAll();
    entityToArea.clear();
    for (const area of areas) {
      if (!area.location) continue;
      const [lon, lat] = area.location.coordinates;
      const position = Cesium.Cartesian3.fromDegrees(lon, lat);
      const marker = ds.entities.add({
        id: `${STAGING_PREFIX}${area.id}`,
        position,
        billboard: {
          image: glyphUrl,
          width: 22,
          height: 22,
          // Ambient: slightly transparent so it reads as context, never an alert.
          color: Cesium.Color.WHITE.withAlpha(0.9),
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
          verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
        },
        label: {
          text: area.name,
          font: '11px sans-serif',
          fillColor: Cesium.Color.fromCssColorString(STAGING_AMBIENT_COLOR),
          showBackground: true,
          backgroundColor: Cesium.Color.fromCssColorString('#0b1220').withAlpha(0.6),
          pixelOffset: new Cesium.Cartesian2(0, 6),
          verticalOrigin: Cesium.VerticalOrigin.TOP,
          scaleByDistance: new Cesium.NearFarScalar(5.0e5, 1.0, 4.0e6, 0.0),
        },
      });
      entityToArea.set(marker, area);
    }
  };

  const refreshNow = async (bbox: BboxQuery): Promise<void> => {
    if (destroyed) return;
    if (inflight) inflight.abort();
    const ctl = new AbortController();
    inflight = ctl;
    const gen = ++loadGen;
    try {
      const res = await fetcher(bbox, ctl.signal);
      if (destroyed || ctl.signal.aborted || gen !== loadGen) return;
      render(res.staging_areas);
      opts.onCount?.(res.staging_areas.length);
      ds.show = opts.getVisible ? opts.getVisible() : true;
    } catch (err) {
      if ((err as { name?: string }).name === 'AbortError') return;
      console.error('fetchStagingAreasInBbox failed', err);
    }
  };

  return {
    requestRefresh(bbox: BboxQuery): void {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => void refreshNow(bbox), REFRESH_DEBOUNCE_MS);
    },
    refreshNow,
    setVisible(visible: boolean): void {
      ds.show = visible;
    },
    pick(entity: unknown): StagingArea | null {
      if (!entity || typeof entity !== 'object') return null;
      return entityToArea.get(entity as Cesium.Entity) ?? null;
    },
    destroy(): void {
      destroyed = true;
      if (debounceTimer) clearTimeout(debounceTimer);
      if (inflight) inflight.abort();
      entityToArea.clear();
      if (!viewer.isDestroyed()) viewer.dataSources.remove(ds, true);
    },
  };
}
